import { injectable, inject } from 'inversify';
import { Action } from '@openfin/predux';
import { Identity } from 'openfin/_v2/main';

import { Inject } from '../common/Injectables';
import { StoredNotification } from '../model/StoredNotification';
import {
  CreateNotification,
  RemoveNotifications,
  ToggleCenterVisibility,
  ToggleCenterMuted,
  BlurCenter,
  UpdateNotification,
} from '../store/Actions';
import { ServiceStore } from '../store/ServiceStore';
import { RootState } from '../store/State';
import { delay } from '../utils/delay';
import { NOTIFICATION_CENTER_ANIMATION_DURATION } from '../constants';
import { Toast } from '../model/Toast';
import { NotificationSource } from '../../client';
import { StoredApplication } from '../model/Environment';
import { SERVICE_IDENTITY } from '../../client/internal';
import { getStickyNotifications, MinimizeToast } from '../store/partials/Notification';

import { Layouter } from './Layouter';
import { LayoutStack } from './LayoutStack';
import { AsyncInit } from './AsyncInit';
import { LayoutableWindowState, LayoutableWindow } from './LayoutableWindow';
import { WindowManager } from './WindowManager';
import { ToastStateHandler } from './ToastStateHandler';

export type NotificationWindowInfo = ToastInfo | StickyToastInfo;

export interface ToastInfo {
  id: string;
  identity: Identity;
  state: LayoutableWindowState;
  notification: StoredNotification;
  sticky: false;
}

export interface StickyToastInfo extends Omit<ToastInfo, 'sticky'> {
  sticky: true;
  stack: StoredNotification[];
}

@injectable()
export class ToastManager extends AsyncInit {
  private readonly _layouter: Layouter;
  private readonly _store: ServiceStore;
  private readonly _windowManager: WindowManager;
  private readonly _stack: LayoutStack;
  private readonly _stateHandler: ToastStateHandler;

  constructor(
    @inject(Inject.STORE) store: ServiceStore,
    @inject(Inject.LAYOUTER) layouter: Layouter,
    @inject(Inject.WINDOW_MANAGER) windowManager: WindowManager,
    @inject(Inject.TOAST_STATE_HANDLER) stateHandler: ToastStateHandler,
    @inject(Inject.LAYOUT_STACK) stack: LayoutStack
  ) {
    super();

    this._store = store;
    this._store.onAction.add(this.onAction, this);
    this._layouter = layouter;
    this._layouter.onLayoutRequired.add(this.onLayoutRequired, this);
    this._windowManager = windowManager;
    this._stateHandler = stateHandler;
    this._stack = stack;
  }

  protected async init(): Promise<void> {
    await this._store.initialized;
    await this._windowManager.initialized;

    // Recreate sticky toasts
    await Promise.all(
      getStickyNotifications(this._store.state)
        .sort((a, b) => b.notification.date.valueOf() - a.notification.date.valueOf())
        .map((n) => this.create(n))
    );
  }

  /**
   * Get the toast associated with a notification.
   * This is used in tests to get the openfin window associated with a toast.
   */
  public getToastWindowInfo(nid: string): NotificationWindowInfo | undefined {
    const toast = this._stack.getToast(nid) as Toast;

    if (!toast) {
      return undefined;
    }

    const { id, state, windowName, notification } = toast;
    const info: ToastInfo = {
      id,
      identity: { uuid: SERVICE_IDENTITY.uuid, name: windowName },
      state,
      notification,
      sticky: false,
    };

    if (toast?.isSticky) {
      const stack = this._stack.stickyStack.notifications;
      const stickyInfo: StickyToastInfo = {
        ...info,
        sticky: true,
        stack,
      };

      return stickyInfo;
    }

    return info;
  }

  public getAllToastsWindowInfo(): NotificationWindowInfo[] {
    // Currently windows are created when a toast is spawned so we can just return everything in the stack.
    // In the future when we create windows as they are needed this should change to only return the windows
    // that have been created
    const items: NotificationWindowInfo[] = [];

    this._stack.items.forEach((item) => {
      const info = this.getToastWindowInfo(item.id);

      if (info) {
        items.push(info);
      }
    });

    return items;
  }

  /**
   * Signal callback for layout required event. This signal is emitted by Layouter in events like
   * monitor info is changed, etc.
   */
  private onLayoutRequired(): void {
    this._layouter.layout(this._stack);
  }

  private async onAction(action: Action<RootState>): Promise<void> {
    if (action instanceof CreateNotification) {
      const { notification } = action;
      if (
        !this._store.state.centerMuted &&
        !this.isMuted(action.notification.source, notification.notification.stream?.id)
      ) {
        this.create(action.notification);
      }
    } else if (action instanceof RemoveNotifications) {
      this.notificationChange(...action.notifications);
    } else if (action instanceof ToggleCenterMuted) {
      if (this._store.state.centerMuted) {
        this._stack.items.forEach((win: LayoutableWindow) => this.retireWindow(win));
      }
    } else if (action instanceof BlurCenter) {
      if (!this._store.state.centerVisible) {
        this.showStickyStack();
      }
    } else if (action instanceof ToggleCenterVisibility) {
      if (this._store.state.centerVisible) {
        this.closeAll();
      } else {
        this.showStickyStack();
        // Get sticky toasts created whilst the center was open
        this._stateHandler.checkQueue();
      }
    } else if (action instanceof MinimizeToast) {
      this.notificationChange(action.notification);
    } else if (action instanceof UpdateNotification) {
      const { updatedNotification } = action;
      if (updatedNotification) {
        const existingToast = this._stack.getToast(updatedNotification.id) as Toast | null;
        if (existingToast) {
          existingToast.notification = updatedNotification;
          this._layouter.onLayoutRequired.emit();
        }
      }
    }
  }

  private async showStickyStack() {
    await delay(NOTIFICATION_CENTER_ANIMATION_DURATION + 100);
    if (this._stack.stickyStack.notifications.length > 0) {
      this._stack.stickyStack.toast?.fadeIn();
    }
  }

  private retireWindow(win: LayoutableWindow): void {
    win.setState(
      win.state >= LayoutableWindowState.ACTIVE
        ? LayoutableWindowState.TRANSITION_OUT
        : LayoutableWindowState.CLOSED
    );
  }

  private notificationChange(...notifications: StoredNotification[]): void {
    notifications.forEach((notification) => {
      if (notification.notification.sticky === 'sticky') {
        const stack = this._stack.stickyStack;
        const stickyNotes = getStickyNotifications(this._store.state);
        stack.update(stickyNotes);
        this._layouter.onLayoutRequired.emit();
      } else {
        const toast: LayoutableWindow | null = this._stack.getToast(notification.id);
        if (toast) {
          this.retireWindow(toast);
        }
      }
    });
  }

  /**
   * Instantly closes all toasts (without transition animation) and resets the stack.
   */
  private async closeAll(): Promise<void> {
    const toasts = [];
    const sticky = [];
    for (const item of this._stack.items) {
      if (item.isSticky) {
        sticky.push(item);
      } else {
        toasts.push(item);
      }
    }

    await Promise.all(sticky.map((t) => t.fadeOut()));

    await Promise.all(toasts.map((toast) => this.closeToast(toast)));

    this._stack.clear();
  }

  /**
   * Create a new toast.
   *
   * Returned promise captures the creation and initialisation of the window that will be used to display the toast,
   * but not anything to do with the presentation of the toast to the user.
   *
   * If the notification re-uses the ID of an existing notification, that window will be closed (including 'close'
   * animation) and re-created as part of the returned promise.
   *
   * @param notification The notification to display in the created toast.
   */
  private async create(notification: StoredNotification): Promise<void> {
    const state = this._store.state;

    // Allow sticky toasts to be created even if the center is open
    if (
      notification.notification.sticky !== 'sticky' &&
      (state.centerVisible || !this._stack.hasCapacity)
    ) {
      return;
    }

    // Check for existing toast with same ID
    // Toast may still exist even after notification is deleted, due to toast animations.
    const existingToast = this._stack.getToast(notification.id) as Toast | null;

    if (existingToast) {
      // Need to wait for the existing toast to close, so that we can re-use that window identity for a new toast
      await existingToast.setState(
        existingToast.state >= LayoutableWindowState.ACTIVE
          ? LayoutableWindowState.TRANSITION_OUT
          : LayoutableWindowState.CLOSED
      );

      if (this._stack.items.includes(existingToast) || this._stack.queue.includes(existingToast)) {
        console.error('Toast should have been removed from queue by now');
      }

      await existingToast.close();
    }

    let toast: Toast | undefined = undefined;

    if (notification.notification.sticky === 'sticky') {
      const stickyStack = this._stack.stickyStack;
      const stickyNotes = getStickyNotifications(this._store.state);
      stickyStack.update(stickyNotes);
      if (!stickyStack.toast) {
        // Create new toast
        toast = new Toast(this._store, this._windowManager, notification);
        stickyStack.toast = toast;
      } else {
        // Relayout toasts as the contents of the sticky toast may be a different size
        this._layouter.onLayoutRequired.emit();
      }
    } else {
      toast = new Toast(this._store, this._windowManager, notification);
    }

    if (toast) {
      this.trackWindow(toast);
    }
  }

  private trackWindow(win: LayoutableWindow): void {
    // Handle state changes
    win.onStateChange.add(this.handleToastStateChange, this);
    this._stack.addToQueue(win);
  }

  private async handleToastStateChange(
    win: LayoutableWindow,
    state: LayoutableWindowState
  ): Promise<void> {
    await this._stateHandler.handleStateChange(win, state);
  }

  /**
   * Instantly closes a toast. Will immediately destroy the toast and its window, use removeToasts to "animate out"
   * the toast.
   *
   * @param toast Toast to delete.
   */
  private async closeToast(toast: LayoutableWindow): Promise<void> {
    if (this._stack.existsWithinStack(toast.id) && this._stack.remove(toast)) {
      if (toast.state < LayoutableWindowState.CLOSED) {
        toast.setState(LayoutableWindowState.CLOSED);
      } else {
        console.warn('Stack contained a toast that was in CLOSED state', toast.id);

        // Sanity check, to ensure toast window is removed
        toast.close();
      }
    } else {
      console.warn('Trying to delete a toast that is not in the stack', toast && toast.id);

      // Toast is likely already closed, but should make sure

      await toast.close();
    }
  }

  private isMuted(source: NotificationSource, streamId: string | undefined): boolean {
    let app: StoredApplication | undefined;
    if (streamId !== undefined) {
      const storedStream = this._store.state.streams.get(streamId);
      if (storedStream) {
        return storedStream.muted;
      }
    }
    if (source.type === 'desktop') {
      app = this._store.state.applications.get(source.identity.uuid);
    }
    if (app) {
      return app.muted;
    }
    return false;
  }
}
